import {
  BascBox,
  BascButton,
  BascCaptcha,
  BascCard,
  BascCenter,
  BascCheckbox,
  BascCountrySelect,
  BascFormErrorSummary,
  BascGroup,
  BascLanguageSelector,
  BascPasswordInput,
  BascPhoneInput,
  BascScrollArea,
  BascSelect,
  BascStack,
  BascStepper,
  BascStepperStep,
  BascText,
  BascTextInput,
  type FormErrorSummaryItem,
  getStatesForCountry,
  useIsMobile,
  useTranslation,
} from '@bco/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { loadClientAssets } from '../../federationInit';
import { buildLoginBackgroundStyle } from '../../utils/loginBackgroundStyle';
import './register.css';

interface ClientAssets {
  appLogo?: string;
  appLogoDark?: string;
  loginBackground?: string;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  contactName: string;
  companyName: string;
  phone: string;
  country: string;
  street: string;
  city: string;
  apartment: string;
  state: string;
  postalCode: string;
  acceptedTerms: boolean;
  captchaToken: string | null;
}

const initialFormData: FormData = {
  email: '',
  password: '',
  confirmPassword: '',
  contactName: '',
  companyName: '',
  phone: '',
  country: '',
  street: '',
  city: '',
  apartment: '',
  state: '',
  postalCode: '',
  acceptedTerms: false,
  captchaToken: null,
};

const CAPTCHA_SITE_KEY = import.meta.env.VITE_CAPTCHA_SITE_KEY;

const TOTAL_STEPS = 4;

/** Stable DOM id for a field's input, used to anchor error-summary links. */
const fieldId = (field: keyof FormData): string => `register-${field}`;

/**
 * Two-up field row: side-by-side on tablet+ (`BascGroup grow`), stacked into a
 * single column on phones so inputs keep a usable width and tap target.
 */
function TwoUp({
  isMobile,
  children,
}: {
  isMobile: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return isMobile ? (
    <BascStack gap="md">{children}</BascStack>
  ) : (
    <BascGroup grow>{children}</BascGroup>
  );
}

export function RegisterPage() {
  const { clientName = '' } = useParams<{ clientName: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [assets, setAssets] = useState<ClientAssets | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function loadAssets() {
      const clientAssets = await loadClientAssets(clientName);
      setAssets(clientAssets);
    }
    if (clientName) {
      loadAssets();
    }
  }, [clientName]);

  useEffect(() => {
    async function checkRegistration() {
      try {
        const gatewayUrl = import.meta.env.VITE_GATEWAY_URL || '';
        const res = await fetch(
          `${gatewayUrl}/api/auth/registration-enabled?clientName=${encodeURIComponent(clientName)}`
        );
        if (res.ok) {
          const data = await res.json();
          if (!data.registrationEnabled) {
            navigate(`/${clientName}/login`, { replace: true });
          }
        }
      } catch {
        // Allow page to load if check fails; backend will still reject
      }
    }
    if (clientName) {
      checkRegistration();
    }
  }, [clientName, navigate]);

  useEffect(() => {
    setTimeout(() => firstInputRef.current?.focus(), 50);
  }, [activeStep]);

  const updateFormData = <K extends keyof FormData>(
    field: K,
    value: FormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    switch (step) {
      case 0:
        if (!formData.email) {
          newErrors.email = t('validation.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = t('validation.emailInvalid');
        }
        if (!formData.password) {
          newErrors.password = t('validation.passwordRequired');
        } else if (formData.password.length < 8) {
          newErrors.password = t('validation.passwordMinLength', { min: 8 });
        } else if (
          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
            formData.password
          )
        ) {
          newErrors.password = t('register.passwordStrong');
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = t('validation.passwordsDoNotMatch');
        }
        break;

      case 1:
        if (!formData.contactName) {
          newErrors.contactName = t('validation.nameRequired');
        }
        if (!formData.companyName) {
          newErrors.companyName = t('validation.companyRequired');
        }
        if (!formData.phone) {
          newErrors.phone = t('validation.phoneRequired');
        }
        break;

      case 2:
        if (!formData.country) {
          newErrors.country = t('validation.countryRequired');
        }
        if (!formData.street) {
          newErrors.street = t('validation.streetRequired');
        }
        if (!formData.city) {
          newErrors.city = t('validation.cityRequired');
        }
        break;

      case 3:
        if (!formData.acceptedTerms) {
          newErrors.acceptedTerms = t('register.mustAcceptTerms');
        }
        if (!formData.captchaToken) {
          newErrors.captchaToken = t('register.completeCaptcha');
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeStep < 3) {
      handleNext();
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_GATEWAY_URL || ''}/api/auth/register`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            clientName,
            contactName: formData.contactName,
            companyName: formData.companyName,
            phone: formData.phone,
            address: {
              country: formData.country,
              street: formData.street,
              city: formData.city,
              apartment: formData.apartment || undefined,
              state: formData.state || undefined,
              postalCode: formData.postalCode || undefined,
            },
            captchaToken: formData.captchaToken,
            acceptedTerms: formData.acceptedTerms,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || t('errors.registrationFailed'));
      }

      setIsSuccess(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t('errors.registrationFailed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Error summary for the active step: inline field errors anchored to their
  // inputs, plus the server/non-field submit error rendered as plain text.
  const summaryErrors: FormErrorSummaryItem[] = [
    ...(Object.keys(errors) as (keyof FormData)[])
      .filter((field) => Boolean(errors[field]))
      .map((field) => ({
        fieldId: fieldId(field),
        message: errors[field] as string,
      })),
    ...(submitError ? [{ message: submitError }] : []),
  ];

  const stateOptions = getStatesForCountry(formData.country).map(
    (s: { code: string; name: string }) => ({
      value: s.code,
      label: s.name,
    })
  );

  const backgroundStyle = buildLoginBackgroundStyle(assets);

  if (isSuccess) {
    return (
      <BascBox className="register-page" style={backgroundStyle}>
        <BascBox className="register-overlay" />
        <BascCenter className="register-container">
          <BascCard
            className="register-card register-card--success"
            shadow="xl"
            padding="xl"
            radius="md"
          >
            <BascStack gap="lg" align="center">
              {assets?.appLogo && (
                <BrandLogo
                  src={assets.appLogo}
                  srcDark={assets.appLogoDark}
                  alt="BCO PORTAL"
                  className="register-logo"
                />
              )}
              <BascText textRole="heading1" fw={600} ta="center">
                {t('auth.checkEmail')}
              </BascText>
              <BascText
                ta="center"
                c="dimmed"
                dangerouslySetInnerHTML={{
                  __html: t('auth.checkEmailDesc', { email: formData.email }),
                }}
              />
              <BascButton
                variant="subtle"
                onClick={() => navigate(`/${clientName}/login`)}
              >
                {t('backToLogin')}
              </BascButton>
            </BascStack>
          </BascCard>
        </BascCenter>
      </BascBox>
    );
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <BascStack gap="md">
            <BascTextInput
              ref={firstInputRef}
              id={fieldId('email')}
              label={t('auth.email')}
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.currentTarget.value)}
              error={errors.email}
              required
              type="email"
            />
            <BascPasswordInput
              id={fieldId('password')}
              label={t('auth.password')}
              placeholder=""
              value={formData.password}
              onChange={(e) =>
                updateFormData('password', e.currentTarget.value)
              }
              error={errors.password}
              required
              description={t('register.passwordRequirements')}
            />
            <BascPasswordInput
              id={fieldId('confirmPassword')}
              label={t('auth.confirmPassword')}
              placeholder=""
              value={formData.confirmPassword}
              onChange={(e) =>
                updateFormData('confirmPassword', e.currentTarget.value)
              }
              error={errors.confirmPassword}
              required
            />
          </BascStack>
        );
      case 1:
        return (
          <BascStack gap="md">
            <BascTextInput
              ref={firstInputRef}
              id={fieldId('contactName')}
              label={t('auth.name')}
              placeholder=""
              value={formData.contactName}
              onChange={(e) =>
                updateFormData('contactName', e.currentTarget.value)
              }
              error={errors.contactName}
              required
            />
            <BascTextInput
              id={fieldId('companyName')}
              label={t('register.companyName')}
              placeholder=""
              value={formData.companyName}
              onChange={(e) =>
                updateFormData('companyName', e.currentTarget.value)
              }
              error={errors.companyName}
              required
            />
            <BascBox id={fieldId('phone')}>
              <BascPhoneInput
                label={t('auth.phone')}
                value={formData.phone}
                onChange={(value) => updateFormData('phone', value)}
                error={errors.phone}
                required
                defaultCountry="US"
              />
            </BascBox>
          </BascStack>
        );
      case 2:
        return (
          <BascStack gap="md">
            <BascCountrySelect
              id={fieldId('country')}
              label={t('auth.country')}
              placeholder=""
              value={formData.country}
              onChange={(value) => {
                updateFormData('country', value || '');
                updateFormData('state', '');
              }}
              error={errors.country}
              required
            />
            <BascTextInput
              id={fieldId('street')}
              label={t('register.streetAddress')}
              placeholder=""
              value={formData.street}
              onChange={(e) => updateFormData('street', e.currentTarget.value)}
              error={errors.street}
              required
            />
            <TwoUp isMobile={isMobile}>
              <BascTextInput
                id={fieldId('city')}
                label={t('register.city')}
                placeholder=""
                value={formData.city}
                onChange={(e) => updateFormData('city', e.currentTarget.value)}
                error={errors.city}
                required
              />
              <BascTextInput
                id={fieldId('apartment')}
                label={t('register.apartment')}
                placeholder=""
                value={formData.apartment}
                onChange={(e) =>
                  updateFormData('apartment', e.currentTarget.value)
                }
              />
            </TwoUp>
            <TwoUp isMobile={isMobile}>
              {stateOptions.length > 0 ? (
                <BascSelect
                  id={fieldId('state')}
                  label={t('register.stateProvince')}
                  placeholder=""
                  data={stateOptions}
                  value={formData.state}
                  onChange={(value) => updateFormData('state', value || '')}
                  searchable
                />
              ) : (
                <BascTextInput
                  id={fieldId('state')}
                  label={t('register.stateProvince')}
                  placeholder=""
                  value={formData.state}
                  onChange={(e) =>
                    updateFormData('state', e.currentTarget.value)
                  }
                />
              )}
              <BascTextInput
                id={fieldId('postalCode')}
                label={t('register.postalCode')}
                placeholder=""
                value={formData.postalCode}
                onChange={(e) =>
                  updateFormData('postalCode', e.currentTarget.value)
                }
              />
            </TwoUp>
          </BascStack>
        );
      case 3:
        return (
          <BascStack gap="md">
            <BascBox className="terms-box">
              <BascText textRole="title" mb="xs">
                {t('register.termsTitle')}
              </BascText>
              <BascText textRole="body" c="dimmed">
                {t('register.termsDesc')}
              </BascText>
            </BascBox>

            <BascCheckbox
              id={fieldId('acceptedTerms')}
              label={t('register.acceptTerms')}
              checked={formData.acceptedTerms}
              onChange={(e) =>
                updateFormData('acceptedTerms', e.currentTarget.checked)
              }
              error={errors.acceptedTerms}
            />

            <BascBox id={fieldId('captchaToken')}>
              <BascCaptcha
                siteKey={CAPTCHA_SITE_KEY}
                onChange={(token) => updateFormData('captchaToken', token)}
                error={errors.captchaToken}
              />
            </BascBox>
          </BascStack>
        );
      default:
        return null;
    }
  };

  return (
    <BascBox className="register-page" style={backgroundStyle}>
      <BascBox className="register-overlay" />
      <BascCenter className="register-container">
        <BascCard className="register-card" shadow="xl" padding={0} radius="md">
          {/* Header: Logo + Title (full width) */}
          <BascBox className="register-header">
            <BascGroup gap="md" align="center">
              {assets?.appLogo && (
                <BrandLogo
                  src={assets.appLogo}
                  srcDark={assets.appLogoDark}
                  alt="BCO PORTAL"
                  className="register-logo"
                />
              )}
              <BascText textRole="heading2" fw={600}>
                {t('register.title', {
                  client:
                    clientName.charAt(0).toUpperCase() + clientName.slice(1),
                })}
              </BascText>
            </BascGroup>
          </BascBox>

          <form onSubmit={handleFormSubmit} className="register-form">
            {/* Main: vertical stepper + content on tablet+, single column on phones */}
            <BascBox className="register-main">
              {/* Stepper */}
              <BascBox className="register-stepper-column">
                {isMobile && (
                  <BascText textRole="title" mb="xs">
                    {t('register.stepProgress', {
                      current: activeStep + 1,
                      total: TOTAL_STEPS,
                    })}
                  </BascText>
                )}
                <BascStepper
                  active={activeStep}
                  orientation={isMobile ? 'horizontal' : 'vertical'}
                  size={isMobile ? 'sm' : undefined}
                >
                  <BascStepperStep
                    label={t('register.stepAccount')}
                    description={
                      isMobile ? undefined : t('register.stepAccountDesc')
                    }
                  />
                  <BascStepperStep
                    label={t('register.stepContact')}
                    description={
                      isMobile ? undefined : t('register.stepContactDesc')
                    }
                  />
                  <BascStepperStep
                    label={t('register.stepAddress')}
                    description={
                      isMobile ? undefined : t('register.stepAddressDesc')
                    }
                  />
                  <BascStepperStep
                    label={t('register.stepComplete')}
                    description={
                      isMobile ? undefined : t('register.stepCompleteDesc')
                    }
                  />
                </BascStepper>
              </BascBox>

              {/* Content */}
              <BascBox className="register-content-column">
                <BascScrollArea className="register-scroll-area">
                  <BascStack gap="md">
                    <BascFormErrorSummary errors={summaryErrors} />
                    {renderStepContent()}
                  </BascStack>
                </BascScrollArea>
              </BascBox>
            </BascBox>

            {/* Footer (sticky bottom bar on phones) */}
            <BascBox className="register-footer">
              <BascGroup justify="space-between">
                <BascButton
                  type="button"
                  variant="subtle"
                  onClick={
                    activeStep === 0
                      ? () => navigate(`/${clientName}/login`)
                      : handleBack
                  }
                  disabled={isSubmitting}
                >
                  {activeStep === 0 ? t('backToLogin') : t('back')}
                </BascButton>

                <BascGroup gap="md">
                  <BascLanguageSelector compact label={t('language.change')} />
                  <BascButton
                    emphasis="primary"
                    type="submit"
                    loading={activeStep === 3 && isSubmitting}
                  >
                    {activeStep < 3 ? t('next') : t('auth.createAccount')}
                  </BascButton>
                </BascGroup>
              </BascGroup>
            </BascBox>
          </form>
        </BascCard>
      </BascCenter>
    </BascBox>
  );
}

export default RegisterPage;
